/**
 * Drawn ZONES on a board estimate: a closed shape (polygon `line` or freehand `freedraw`) tagged
 * `customData.estimate = { v: 1, role: 'zone', name }`. Fixtures inside it get that ZONE in the
 * AREA/ZONE BOM.
 *
 * Mirror of backend/apps/takeoff/zones.py — keep the two identical. Both test suites read
 * backend/tests/fixtures/zones_crosscheck.json and must give its answers.
 *   - count symbols: the symbol's centre (polygon symbols: their points' box centre; x / y is the first point)
 *   - runs / runs counted along: the zone holding > 50 % of the line's length (sampled every 4 scene px)
 *   - areas: the centroid of the outline
 *   - nested zones: the smallest containing zone wins
 */
import { scenePoints, type GeomEl } from './measure';

export const ZONE_ROLE = 'zone';
const SAMPLE_PX = 4;

type P = [number, number];

export interface ZoneData {
  v: 1;
  role: 'zone';
  name: string;
  /** Bid scope for fixtures inside it ('' / absent = inherit from type / frame). */
  scope?: string;
  /** Hidden on the canvas (opacity 0, locked) but still counted; `hidden_from` restores it exactly. */
  hidden?: boolean;
  hidden_from?: { opacity: number; locked: boolean };
}

export interface DrawnZone {
  id: string;
  name: string;
  pts: P[];
  area: number;
  scope: string;
  hidden: boolean;
}

type El = GeomEl & { isDeleted?: boolean };

const estOf = (el: { customData?: unknown }) => (el.customData as { estimate?: Record<string, unknown> } | null | undefined)?.estimate;

export function isZone(el: El): boolean {
  const est = estOf(el);
  return !!est && est.role === ZONE_ROLE && !el.isDeleted && (el.type === 'line' || el.type === 'freedraw') && (el.points?.length ?? 0) >= 3;
}

export function zoneOf(el: { customData?: unknown; isDeleted?: boolean }): ZoneData | null {
  if (el.isDeleted) return null;
  const est = estOf(el);
  return est && est.role === ZONE_ROLE ? (est as unknown as ZoneData) : null;
}

export function zoneName(est: { name?: unknown } | null | undefined, index: number): string {
  const n = String(est?.name ?? '').trim();
  return (n || `ZONE ${index + 1}`).toUpperCase();
}

export function polygonArea(pts: readonly P[]): number {
  let a = 0;
  for (let i = 0; i < pts.length; i++) {
    const [x1, y1] = pts[i];
    const [x2, y2] = pts[(i + 1) % pts.length];
    a += x1 * y2 - x2 * y1;
  }
  return Math.abs(a) / 2;
}

/** Even-odd ray cast; points on an edge count as inside. */
export function pointInPolygon(pt: readonly [number, number], poly: readonly P[]): boolean {
  const [x, y] = pt;
  let inside = false;
  const n = poly.length;
  for (let i = 0; i < n; i++) {
    const [x1, y1] = poly[i];
    const [x2, y2] = poly[(i + 1) % n];
    const cross = (x2 - x1) * (y - y1) - (y2 - y1) * (x - x1);
    if (Math.abs(cross) < 1e-9 && Math.min(x1, x2) - 1e-9 <= x && x <= Math.max(x1, x2) + 1e-9 && Math.min(y1, y2) - 1e-9 <= y && y <= Math.max(y1, y2) + 1e-9) return true;
    if (y1 > y !== y2 > y) {
      const xin = x1 + ((y - y1) * (x2 - x1)) / (y2 - y1);
      if (x < xin) inside = !inside;
    }
  }
  return inside;
}

/** Drawn zones in scene order (deleted ones skipped), with absolute outline points. */
export function zonesOf(scene: readonly El[]): DrawnZone[] {
  const out: DrawnZone[] = [];
  for (const e of scene) {
    if (!isZone(e)) continue;
    let pts = scenePoints(e).map((p) => [p[0], p[1]] as P);
    if (pts.length > 3 && Math.hypot(pts[0][0] - pts[pts.length - 1][0], pts[0][1] - pts[pts.length - 1][1]) < 1e-6) pts = pts.slice(0, -1);
    if (pts.length < 3) continue;
    const est = (estOf(e) ?? {}) as { name?: unknown; scope?: unknown; hidden?: unknown };
    out.push({ id: e.id, name: zoneName(est, out.length), pts, area: polygonArea(pts), scope: String(est.scope ?? '').trim(), hidden: !!est.hidden });
  }
  return out;
}

export function zoneAt(pt: readonly [number, number], zones: readonly DrawnZone[]): DrawnZone | null {
  let best: DrawnZone | null = null;
  for (const z of zones) if (pointInPolygon(pt, z.pts) && (!best || z.area < best.area)) best = z;
  return best;
}

function isSymbolPolygon(el: El): boolean {
  const est = estOf(el);
  return el.type === 'line' && est?.role === 'fixture' && ((est.measure as string | undefined) ?? 'count') === 'count' && !est.along_run;
}

function polylineMidpoint(pts: readonly P[]): P {
  if (pts.length === 1) return pts[0];
  const segs = pts.slice(1).map((p, i) => Math.hypot(p[0] - pts[i][0], p[1] - pts[i][1]));
  let half = segs.reduce((a, b) => a + b, 0) / 2;
  for (let i = 1; i < pts.length; i++) {
    const d = segs[i - 1];
    if (half <= d && d > 0) {
      const t = half / d;
      return [pts[i - 1][0] + (pts[i][0] - pts[i - 1][0]) * t, pts[i - 1][1] + (pts[i][1] - pts[i - 1][1]) * t];
    }
    half -= d;
  }
  return pts[pts.length - 1];
}

/** A fixture's reference point (same as the server's board.centre). */
export function symbolCentre(el: El): P {
  const pts = el.points;
  if (isSymbolPolygon(el) && pts?.length) {
    const xs = pts.map((p) => p[0]);
    const ys = pts.map((p) => p[1]);
    return [el.x + (Math.min(...xs) + Math.max(...xs)) / 2, el.y + (Math.min(...ys) + Math.max(...ys)) / 2];
  }
  if ((el.type === 'arrow' || el.type === 'line') && pts?.length) {
    const [mx, my] = polylineMidpoint(pts.map((p) => [p[0], p[1]] as P));
    return [el.x + mx, el.y + my];
  }
  return [el.x + el.width / 2, el.y + el.height / 2];
}

function centroid(pts: readonly P[]): P {
  let a = 0;
  let cx = 0;
  let cy = 0;
  for (let i = 0; i < pts.length; i++) {
    const [x1, y1] = pts[i];
    const [x2, y2] = pts[(i + 1) % pts.length];
    const c = x1 * y2 - x2 * y1;
    a += c;
    cx += (x1 + x2) * c;
    cy += (y1 + y2) * c;
  }
  if (Math.abs(a) < 1e-9) return [pts.reduce((s, p) => s + p[0], 0) / pts.length, pts.reduce((s, p) => s + p[1], 0) / pts.length];
  return [cx / (3 * a), cy / (3 * a)];
}

/** The drawn zone a fixture belongs to, or null. */
export function fixtureZone(el: El, measure: string, zones: readonly DrawnZone[]): DrawnZone | null {
  if (!zones.length) return null;
  const centre = symbolCentre(el);
  if (measure === 'area') {
    const pts = scenePoints(el).map((p) => [p[0], p[1]] as P);
    return zoneAt(pts.length >= 3 ? centroid(pts) : centre, zones);
  }
  const runLike = (el.type === 'line' || el.type === 'arrow') && !isSymbolPolygon(el);
  if (runLike && (el.points?.length ?? 0) >= 2) {
    const pts = scenePoints(el);
    let total = 0;
    const by = new Map<string, { z: DrawnZone; w: number }>();
    for (let i = 1; i < pts.length; i++) {
      const [x1, y1] = pts[i - 1];
      const [x2, y2] = pts[i];
      const d = Math.hypot(x2 - x1, y2 - y1);
      if (d <= 0) continue;
      const n = Math.max(1, Math.ceil(d / SAMPLE_PX));
      for (let k = 0; k < n; k++) {
        const t = (k + 0.5) / n;
        total += d / n;
        const z = zoneAt([x1 + (x2 - x1) * t, y1 + (y2 - y1) * t], zones);
        if (z) by.set(z.id, { z, w: (by.get(z.id)?.w ?? 0) + d / n });
      }
    }
    if (total <= 0) return zoneAt(centre, zones);
    let best: { z: DrawnZone; w: number } | null = null;
    for (const v of by.values()) if (!best || v.w > best.w) best = v;
    return best && best.w > total / 2 ? best.z : null;
  }
  return zoneAt(centre, zones);
}

/** Fixtures (placements) per drawn zone id, for the overlay labels and the panel. */
export function fixturesByZone(scene: readonly El[], zones: readonly DrawnZone[] = zonesOf(scene)): Map<string, string[]> {
  const out = new Map<string, string[]>(zones.map((z) => [z.id, []]));
  for (const e of scene) {
    if (e.isDeleted) continue;
    const est = estOf(e);
    if (est?.role !== 'fixture') continue;
    const z = fixtureZone(e, (est.measure as string | undefined) ?? (e.type === 'line' || e.type === 'arrow' ? 'linear' : 'count'), zones);
    if (z) out.get(z.id)!.push(e.id);
  }
  return out;
}

/** Next default name: "ZONE n" not already used. */
export function nextZoneName(scene: readonly El[]): string {
  const used = new Set(zonesOf(scene).map((z) => z.name));
  for (let i = 1; ; i++) if (!used.has(`ZONE ${i}`)) return `ZONE ${i}`;
}

/** Ramer–Douglas–Peucker: simplify a freehand outline (scene px tolerance). */
export function simplify(pts: readonly P[], tol = 2): P[] {
  if (pts.length <= 3) return pts.map((p) => [p[0], p[1]] as P);
  const keep = new Array<boolean>(pts.length).fill(false);
  keep[0] = keep[pts.length - 1] = true;
  const stack: [number, number][] = [[0, pts.length - 1]];
  while (stack.length) {
    const [a, b] = stack.pop()!;
    let idx = -1;
    let dmax = tol;
    const [ax, ay] = pts[a];
    const [bx, by] = pts[b];
    const len = Math.hypot(bx - ax, by - ay) || 1;
    for (let i = a + 1; i < b; i++) {
      const d = Math.abs((bx - ax) * (ay - pts[i][1]) - (ax - pts[i][0]) * (by - ay)) / len;
      if (d > dmax) {
        dmax = d;
        idx = i;
      }
    }
    if (idx >= 0) {
      keep[idx] = true;
      stack.push([a, idx], [idx, b]);
    }
  }
  return pts.filter((_, i) => keep[i]).map((p) => [p[0], p[1]] as P);
}
